import { FC, useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { Link } from "react-router-dom";
import { FormInstance, Input, Modal, Form as AntForm } from "antd";
import {
  AppButton,
  AuthLayout,
  Form,
  FORM_FIELD_TYPES,
} from "site/ui/base";
import { routeNames } from "utils";
import { FlexBoxHorizontal, FlexBoxVertical } from "common/base/flex-box";
import { AppText } from "common/base/text/index";
import { IoCheckmarkCircle, IoRefreshCircleOutline } from "react-icons/io5";
import { Loader } from "common";
import { useNotification } from "providers";
const ButtonContainer = styled.div`
  margin-top: 0.5rem;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 2rem 0;
  color: #666;
  font-size: 0.875rem;

  &::before,
  &::after {
    content: "";
    flex: 1;
    border-bottom: 1px solid #e2e8f0;
  }

  &::before {
    margin-right: 1rem;
  }

  &::after {
    margin-left: 1rem;
  }
`;

const SocialButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SocialButtonWrapper = styled(AppButton)`
  flex: 1;
  background: white !important;
  border: 1px solid #e2e8f0 !important;
  color: #333 !important;
  box-shadow: none !important;
  padding: 12px 24px !important;
  font-family: "Manrope", sans-serif !important;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: #f8f9fa !important;
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const LoginText = styled.p`
  text-align: center;
  margin-top: 2rem;
  font-size: 0.875rem;
  color: #666;

  a {
    color: #5575f7;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;
export interface ISignUpData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}
export interface SignUpProps extends Pick<VerifyEmailProp, "onSendOtp" | "onVerifyOtp"> {
  onMoveToPersonalization: (data: ISignUpData, verificationToken: string) => Promise<void>
}
export const SignUp:FC<SignUpProps> = ({
  onMoveToPersonalization, onSendOtp, onVerifyOtp
}) => {
  const formRef = useRef<FormInstance<ISignUpData>>(null);
  const [loading, setLoading] = useState(false)
  const [errorData, setErrorData] = useState<Partial<Record<keyof ISignUpData, string>>>({
    
  })
  const notification = useNotification();
  const [form, setForm] = useState<FormInstance<ISignUpData>|undefined>();
  const email = AntForm.useWatch(["email"], form); 
  const [otpVerificationToken, setOtpVerificationToken] = useState("");

  const validateForm = (form: ISignUpData, notify?: boolean): boolean => {
    if(!form.email?.trim()){
      if(notify){
        setErrorData({email: "Email is required"})
      }
      return false
    }
    if(!form.password?.trim()){
      if(notify){
        setErrorData({password: "Password is required"})
      }
      return false
    }
    if(form.password !== form.confirmPassword){
      if(notify){
        setErrorData({password: "Password mismatch"})
      }
      return false
    }
    if(!form.firstName?.trim()){
      if(notify){
        setErrorData({firstName: "First name is required"})
      }
      return false
    }
    if(!form.lastName?.trim()){
      if(notify){
        setErrorData({lastName: "Last is required"})
      }
      return false
    }
    return true
  }
  return (
    <AuthLayout title="Create an account">
      <Form
        formRef={formRef}
        {...{
          getForm(form) {
              setForm(form);        
          },
        }}
        items={[
          {
            fieldType: FORM_FIELD_TYPES.TEXT,
            itemProps: {
              name: "firstName",
              label: "First name",
              rules: [{ required: true, message: "Please input your first name!" }],
            },
            fieldProps: {
              placeholder: "Esosa",
              size: "large",
              
            },
          },
          {
            fieldType: FORM_FIELD_TYPES.TEXT,
            itemProps: {
              name: "lastName",
              label: "Last name",
              rules: [{ required: true, message: "Please input your last name!" }],
            },
            fieldProps: {
              placeholder: "Chiejine",
              size: "large",
            },
          },
          {
            fieldType: FORM_FIELD_TYPES.TEXT,
            itemProps: {
              name: "email",
              label: "Email address",
              rules: [{ required: true, message: "Please input your email!" }],
              extra: <VerifyEmail {...{
                  email,
                  onSendOtp,
                  async onVerifyOtp(email, token) {
                      const res = await onVerifyOtp(email, token)
                      if(res){
                          setOtpVerificationToken(res)
                      }
                      return res
                  },
              }}/>
            },
            fieldProps: {
              placeholder: "esosa@example.art",
              size: "large",
            },
            
          },
          {
            fieldType: FORM_FIELD_TYPES.PASSWORD,
            itemProps: {
              name: "password",
              label: "Password",
              rules: [{ required: true, message: "Please input your password!" }],
            },
            fieldProps: {
              placeholder: "• • • • • • •",
              size: "large",
            },
          },
          {
            fieldType: FORM_FIELD_TYPES.PASSWORD,
            itemProps: {
              name: "confirmPassword",
              label: "Confirm password",
              rules: [
                { required: true, message: "Please confirm your password!" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Passwords do not match!"));
                  },
                }),
              ],
            },
            fieldProps: {
              placeholder: "• • • • • • •",
              size: "large",
            },
          },
        ]}
        formProps={{
          layout: "vertical",
        }}
      />

      <ButtonContainer>
        <AppButton loading={loading} variant="primary" fullWidth onClick={async () => {
          try{
            if(!otpVerificationToken){
              return notification.error({
                message: "Please verify your email address"
              })
            }
            const data = (await formRef.current?.validateFields());
            if(data){
              setLoading(true)
              onMoveToPersonalization(data, otpVerificationToken).finally(() => setLoading(false))
            }
          }catch(e){
            
          }
        }}>
          Create my account
        </AppButton>
      </ButtonContainer>

     {/*  <Divider>Or continue with</Divider>

      <SocialButtonsContainer>
        <SocialButtonWrapper onClick={() => console.log("Google signup")}>
          <img src="/assets/google.png" alt="Google" />
          Continue with Google
        </SocialButtonWrapper>
        <SocialButtonWrapper onClick={() => console.log("Apple signup")}>
          <img src="/assets/apple.png" alt="Apple" />
          Continue with Apple
        </SocialButtonWrapper>
      </SocialButtonsContainer> */}

      <LoginText>
        Already have an account? <Link to={routeNames.login}>Get back in here</Link>
      </LoginText>
    </AuthLayout>
  );
};




// component to verify email by otp
export interface VerifyEmailProp {
    email: string;
    onSendOtp: (email: string) => Promise<boolean|undefined>;
    onVerifyOtp: (email: string, token: string) => Promise<string|undefined>
}
const COUNT_DOWN = 90
export const VerifyEmail:FC<VerifyEmailProp> = ({ email, onSendOtp, onVerifyOtp }) => {
    const [verifyEmailProcessing, setVerifyEmailProcessing] = useState(false);
    const [openVerificationModal, setOpenVerificationModal] = useState(false)
    const [sendOtpProcessing, setSendOtpProcessing] = useState(false);
    const { colors } = useTheme();
    const [verified, setVerified] = useState(false);
    const [countDown, setCountDown] = useState(COUNT_DOWN);

    
    const isValidEmailFormat = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }
    const sendOtp = async (email: string) => {
        setSendOtpProcessing(true);
        const res = await onSendOtp(email)
        setSendOtpProcessing(false)
        if(res){
            setCountDown(COUNT_DOWN)
            setOpenVerificationModal(true)
        }
    }
    const verifyOtp = async (email: string, token: string) => {
        setVerifyEmailProcessing(true)
        const res = await onVerifyOtp(email, token)
        setVerifyEmailProcessing(false)
        if(!res){
            return
        }
        setVerified(true)
        setOpenVerificationModal(false)
    }
    const resendOtp = async (email: string) => {
        setCountDown(COUNT_DOWN)
        await sendOtp(email)
    }
    useEffect(() => {
        return () => {
            setOpenVerificationModal(false)
            setVerified(false)
        }
    }, [])
    useEffect(() => {
        let interval:NodeJS.Timeout | null = null;
        if(openVerificationModal){
          interval = setInterval(() => {
              setCountDown(countDown => countDown > 0 ? countDown - 1 : 0)
          }, 1000)
        }else {
          if(interval){
              clearInterval(interval)
          }
        }
        
        return () => {
            if(interval){
                clearInterval(interval)
            }
        }
    }, [openVerificationModal])
    return  <FlexBoxHorizontal style={{justifyContent: "flex-end", alignItems: "center"}}>
        {verified ? 
            <> 
                <AppText variant="success" weight="medium" size={14}>Verified</AppText>
                <IoCheckmarkCircle size={20} color={colors?.success}  />
                {/** refresh icon to cancel the otp*/}
                <IoRefreshCircleOutline size={20} style={{ 
                    cursor: "pointer"
                }} color={colors?.primary} onClick={() => setVerified(false)}/>

            </>
        : 
            isValidEmailFormat(email) && (
                sendOtpProcessing ? 
                <>
                    <Loader size={14}/> 
                    <AppText variant="primary" weight="medium" size={14}>Sending...</AppText>
                </>:
                <AppText 
                    variant="primary" 
                    cursor="pointer" 
                    weight="medium" 
                    size={14} 
                    onClick={() => sendOtp(email)}>
                        Verify your email
                    </AppText>
                )
        }
        <Modal open={openVerificationModal} footer={null} onCancel={() => setOpenVerificationModal(false)}>
            {verifyEmailProcessing ? <FlexBoxVertical>
                 <FlexBoxVertical style={{ marginBottom: 20, textAlign: "start", alignItems: "center" }}>
                    <AppText weight="bold" align="center" size={24}>Verifying your email</AppText>
                    <AppText variant="textSecondary" align="center">{email}</AppText>
                    <Loader/>
                </FlexBoxVertical>
            </FlexBoxVertical>: 
            <FlexBoxVertical flex gap={40} style={{padding: 20, alignItems: "center", textAlign: "center"}}>
                <FlexBoxVertical style={{ marginBottom: 20, textAlign: "start", alignItems: "center" }}>
                    <AppText weight="bold" align="center" size={24}>Verify your email</AppText>
                    <AppText variant="textSecondary" align="center">Please enter the code we sent to {email}</AppText>
                </FlexBoxVertical>
                <Input.OTP 
                    size="large" 
                    length={4}
                    onChange={(otp) => {
                        if(otp.length === 4){
                            verifyOtp(email, otp)
                        }
                    }}
                />
                <AppText size={14} variant="textSecondary" align="center">Can’t find it? Check your spam folder or click Resend to get a new otp.</AppText>
                <AppText>
                    {countDown > 0 && <AppText size={14} variant="textSecondary" align="center">Resend in {countDown} seconds</AppText>}
                    {countDown == 0 && <AppText disabled={countDown > 0} variant="primary" onClick={() => resendOtp(email)}>Resend</AppText>}
                </AppText>
        </FlexBoxVertical>}
        </Modal>
    </FlexBoxHorizontal>
}
